import React, { useRef, useState } from 'react';
import {
    AlertTriangle, CalendarClock, CheckCircle2, ChevronDown, ClipboardList, ImagePlus, LayoutTemplate, Loader2, MapPin, X,
} from 'lucide-react';
import { Button } from '../Button';
import { ProjectGlobalData, Report, ReportMetadata } from '../../types';
import { resolveProjectCategories, withCurrentValue } from '../../services/projectCategories';
import {
    DatenLuecke, LueckenArt, baueAdresse, zerlegeAdresse, BILD_WARNUNG_TAGE,
} from './projectDataHygiene';
import { uploadProjectMedia, MEDIA_TABLE_MISSING_TEXT } from '../../services/projectMediaService';
import { ProjectMedia } from '../../services/projectReferenceLogic';

/**
 * Rueckfrage beim Oeffnen eines Projekts: was an den Stammdaten fehlt, wird
 * hier DIREKT nachgetragen — nicht nur gemeldet. Wer den Dialog sieht, soll
 * ihn mit einem Klick erledigen koennen, sonst wird er weggeklickt.
 *
 * Alles, was ins Projekt-JSON gehoert (Ende, Beschreibung, Adresse,
 * Kategorie), geht in EINEM onUpdate raus — zwei Aufrufe hintereinander
 * bauen beide auf demselben `report` auf, der zweite ueberschriebe den ersten
 * (dasselbe Muster wie handleParticipantsChange im Hub). Das Bild liegt in
 * `project_media` und wird sofort hochgeladen, unabhaengig vom Speichern.
 *
 * Handy: Bottom-Sheet, volle Breite, Knoepfe untereinander, Eingaben in
 * 16px (darunter zoomt iOS beim Fokussieren die ganze Seite).
 */
interface Props {
    luecken: DatenLuecke[];
    metadata: ReportMetadata;
    globalData: ProjectGlobalData;
    report: Report;
    projectId: string | undefined;
    currentUserName?: string;
    /** Vorhandene Bilder (Reihenfolge/Titelbild fuer den Upload); leer, wenn nicht geladen. */
    mediaItems: ProjectMedia[];
    mediaTabelleFehlt: boolean;
    onUpdate: (report: Report) => void;
    /** Nach Speichern UND nach „Später": der Hub stellt die Frage fuer diese Sitzung zurueck. */
    onClose: () => void;
    /** Ein Bild ist hochgeladen — die Referenzkarte soll neu laden. */
    onMediaChanged: () => void;
}

const TAG_DE = (ymd: string | undefined) => (ymd || '').split('-').reverse().join('.');

const feld = 'w-full px-3 py-2.5 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none text-base sm:text-sm';

/**
 * Auf Modulebene, nicht im Render: eine dort definierte Komponente ist bei
 * jedem Render ein neuer Typ, React haengt ihre Kinder neu ein — und jeder
 * Tastendruck wuerde den Fokus aus dem Eingabefeld werfen.
 */
const Abschnitt: React.FC<{ icon: React.ReactNode; titel: string; text?: string; children?: React.ReactNode; erledigt?: boolean }> =
    ({ icon, titel, text, children, erledigt }) => (
        <section className={`rounded-xl border p-3 sm:p-4 space-y-2 ${erledigt ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-200 bg-slate-50/60'}`}>
            <div className="flex items-center gap-2">
                <span className={`p-1.5 rounded-lg ${erledigt ? 'bg-emerald-600 text-white' : 'bg-amber-100 text-amber-700'}`}>{erledigt ? <CheckCircle2 className="w-4 h-4" /> : icon}</span>
                <h4 className="font-bold text-sm text-slate-800">{titel}</h4>
            </div>
            {text && <p className="text-xs text-slate-600">{text}</p>}
            {children}
        </section>
    );

export const ProjectDataHygieneDialog: React.FC<Props> = ({
    luecken, metadata, globalData, report, projectId, currentUserName,
    mediaItems, mediaTabelleFehlt, onUpdate, onClose, onMediaChanged,
}) => {
    const hat = (art: LueckenArt) => luecken.some(l => l.art === art);
    const endeLuecke = luecken.find(l => l.art === 'ende_fehlt' || l.art === 'ende_naht' || l.art === 'ende_ueberschritten');

    const [endDate, setEndDate] = useState(globalData.endDate || '');
    const [endeBestaetigt, setEndeBestaetigt] = useState(false);
    const [beschreibung, setBeschreibung] = useState(metadata.projectDescription || '');
    const [adresse, setAdresse] = useState(() => zerlegeAdresse(metadata.projectAddress));
    // Katalog aus report.settings (App.withProfileSettings reicht das Profil durch), sonst Standard.
    const projectCategories = resolveProjectCategories(report?.settings);
    const [kategorie, setKategorie] = useState(metadata.projectCategory || metadata.category || '');
    const [unterkategorie, setUnterkategorie] = useState(metadata.projectSubcategory || metadata.subcategory || '');

    const [uploadLaeuft, setUploadLaeuft] = useState(false);
    const [bildHochgeladen, setBildHochgeladen] = useState(false);
    const [bildFehler, setBildFehler] = useState<string | null>(null);
    const [gespeichert, setGespeichert] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const endeGeaendert = endDate !== (globalData.endDate || '');
    const beschreibungGeaendert = beschreibung.trim() !== (metadata.projectDescription || '').trim();
    const neueAdresse = baueAdresse(adresse);
    const adresseGeaendert = neueAdresse !== (metadata.projectAddress || '').trim();
    const kategorieGeaendert = kategorie !== (metadata.projectCategory || metadata.category || '')
        || unterkategorie !== (metadata.projectSubcategory || metadata.subcategory || '');
    const etwasZuSpeichern = endeGeaendert || endeBestaetigt || beschreibungGeaendert || adresseGeaendert || kategorieGeaendert;

    const speichern = () => {
        const nextMeta: ReportMetadata = { ...metadata };
        if (beschreibungGeaendert) nextMeta.projectDescription = beschreibung.trim().slice(0, 2000) || undefined;
        if (adresseGeaendert) nextMeta.projectAddress = neueAdresse;
        if (kategorieGeaendert) {
            nextMeta.projectCategory = kategorie;
            nextMeta.category = kategorie;
            nextMeta.projectSubcategory = unterkategorie;
            nextMeta.subcategory = unterkategorie;
        }
        let nextGlobal: ProjectGlobalData = globalData;
        if (endeGeaendert || endeBestaetigt) {
            const datum = endDate || globalData.endDate;
            nextGlobal = { ...globalData, endDate: datum };
            // Ein neu gesetztes ODER ausdruecklich bestaetigtes Ende gilt als
            // bestaetigt — wer das Datum gerade eingetippt hat, kennt es.
            if (datum) {
                nextGlobal.endDateConfirmation = { endDate: datum, confirmedAt: new Date().toISOString(), confirmedBy: currentUserName || undefined };
            }
        }
        nextMeta.globalData = nextGlobal;
        onUpdate({ ...report, metadata: nextMeta });
        setGespeichert(true);
        onClose();
    };

    const handleFile = async (files: FileList | null) => {
        const file = files?.[0];
        if (!file || !projectId) return;
        setBildFehler(null);
        setUploadLaeuft(true);
        try {
            await uploadProjectMedia(file, projectId, mediaItems);
            setBildHochgeladen(true);
            onMediaChanged();
        } catch (e: any) {
            setBildFehler(e?.message || 'Upload fehlgeschlagen.');
        } finally {
            setUploadLaeuft(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const endeTitel = endeLuecke?.art === 'ende_fehlt'
        ? 'Projektende fehlt'
        : endeLuecke?.art === 'ende_ueberschritten'
            ? `Projektende überschritten (seit ${Math.abs(endeLuecke.tage || 0)} Tagen)`
            : endeLuecke?.tage === 0 ? 'Projekt endet heute' : `Projekt endet in ${endeLuecke?.tage} Tagen`;

    const endeText = endeLuecke?.art === 'ende_fehlt'
        ? 'Ohne geplantes Ende lassen sich Begehungsturnus, Bildpflicht und Auslastung nicht beurteilen.'
        : endeLuecke?.art === 'ende_ueberschritten'
            ? `Das geplante Ende ${TAG_DE(globalData.endDate)} liegt zurück, das Projekt läuft aber noch. Stimmt das Ende noch, oder verlängert sich die Baustelle? Ist sie fertig, bitte den Status in der Projektübersicht auf „Abgeschlossen" setzen.`
            : `Geplantes Ende: ${TAG_DE(globalData.endDate)}. Bitte bestätigen oder anpassen, damit Abrechnung und Referenzblatt stimmen.`;

    return (
        <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4">
            <div className="bg-white w-full sm:max-w-lg max-h-[92vh] sm:max-h-[90vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-scale-in">
                <div className="p-4 sm:p-5 border-b border-slate-100 flex items-start justify-between gap-3 shrink-0">
                    <div className="flex items-start gap-3">
                        <span className="p-2 rounded-xl bg-amber-100 text-amber-700 shrink-0"><AlertTriangle className="w-5 h-5" /></span>
                        <div>
                            <h3 className="font-bold text-slate-800">Projektdaten vervollständigen</h3>
                            <p className="text-xs text-slate-500 mt-0.5">{metadata.projectNumber ? `${metadata.projectNumber} · ` : ''}{metadata.projectName}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" aria-label="Später erinnern"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-4 sm:p-5 space-y-3 overflow-y-auto">
                    {endeLuecke && (
                        <Abschnitt icon={<CalendarClock className="w-4 h-4" />} titel={endeTitel} text={endeText} erledigt={endeBestaetigt || (endeGeaendert && !!endDate)}>
                            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => { setEndDate(e.target.value); setEndeBestaetigt(false); }}
                                    className={`${feld} sm:flex-1`}
                                    aria-label="Geplantes Projektende"
                                />
                                {endeLuecke.art !== 'ende_fehlt' && !endeGeaendert && (
                                    <Button
                                        type="button"
                                        variant={endeBestaetigt ? 'secondary' : 'primary'}
                                        size="md"
                                        className="w-full sm:w-auto"
                                        onClick={() => setEndeBestaetigt(v => !v)}
                                        icon={endeBestaetigt ? <CheckCircle2 className="w-4 h-4" /> : undefined}
                                    >
                                        {endeBestaetigt ? 'Ende bestätigt' : 'Ende stimmt so'}
                                    </Button>
                                )}
                            </div>
                        </Abschnitt>
                    )}

                    {hat('beschreibung') && (
                        <Abschnitt icon={<ClipboardList className="w-4 h-4" />} titel="Projektbeschreibung fehlt" text="Zwei bis vier ganze Sätze: Was wird gebaut, für wen, was ist besonders? Erscheint auf dem Referenzblatt." erledigt={beschreibungGeaendert && !!beschreibung.trim()}>
                            <textarea
                                value={beschreibung}
                                onChange={(e) => setBeschreibung(e.target.value.slice(0, 2000))}
                                rows={3}
                                className={`${feld} resize-y`}
                                placeholder="z. B. Neubau einer Kita mit zwei Gruppenräumen und Außenanlagen …"
                            />
                        </Abschnitt>
                    )}

                    {hat('adresse') && (
                        <Abschnitt icon={<MapPin className="w-4 h-4" />} titel="Projektadresse fehlt" text="Straße, PLZ und Ort der Baustelle. Ohne Adresse keine Karte und kein Anfahrtsweg für Vertreter." erledigt={adresseGeaendert && !!neueAdresse}>
                            <div className="grid grid-cols-1 sm:grid-cols-[1fr_5.5rem_1fr] gap-2">
                                <input value={adresse.street} onChange={(e) => setAdresse({ ...adresse, street: e.target.value })} className={feld} placeholder="Straße Nr." aria-label="Straße" />
                                <input value={adresse.zip} onChange={(e) => setAdresse({ ...adresse, zip: e.target.value.replace(/\D/g, '').slice(0, 5) })} className={feld} placeholder="PLZ" inputMode="numeric" aria-label="PLZ" />
                                <input value={adresse.city} onChange={(e) => setAdresse({ ...adresse, city: e.target.value })} className={feld} placeholder="Ort" aria-label="Ort" />
                            </div>
                        </Abschnitt>
                    )}

                    {hat('kategorie') && (
                        <Abschnitt icon={<LayoutTemplate className="w-4 h-4" />} titel="Kategorie fehlt" text="Die Kategorie sortiert das Projekt in Referenzblätter und Auswertungen ein." erledigt={kategorieGeaendert && !!kategorie}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div className="relative">
                                    <select
                                        value={kategorie}
                                        onChange={(e) => {
                                            const cat = e.target.value;
                                            const subs = projectCategories[cat] || [];
                                            setKategorie(cat);
                                            setUnterkategorie(subs[0] || '');
                                        }}
                                        className={`${feld} appearance-none pr-9`}
                                        aria-label="Kategorie"
                                    >
                                        <option value="">-- Kategorie wählen --</option>
                                        {withCurrentValue(Object.keys(projectCategories), kategorie).map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                    </select>
                                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                </div>
                                <div className="relative">
                                    <select
                                        value={unterkategorie}
                                        onChange={(e) => setUnterkategorie(e.target.value)}
                                        disabled={!kategorie}
                                        className={`${feld} appearance-none pr-9 disabled:bg-slate-100 disabled:text-slate-400`}
                                        aria-label="Unterkategorie"
                                    >
                                        <option value="">-- Unterkategorie --</option>
                                        {withCurrentValue(projectCategories[kategorie] || [], unterkategorie).map(sub => <option key={sub} value={sub}>{sub}</option>)}
                                    </select>
                                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                </div>
                            </div>
                        </Abschnitt>
                    )}

                    {hat('bild') && (
                        <Abschnitt icon={<ImagePlus className="w-4 h-4" />} titel="Projektbild fehlt" text={`Das Projekt endet in weniger als ${BILD_WARNUNG_TAGE / 7} Wochen und hat noch kein Bild für Referenzblatt und Social Media. Am Handy geht auch ein Foto direkt von der Baustelle.`} erledigt={bildHochgeladen}>
                            {mediaTabelleFehlt ? (
                                <p className="text-xs text-rose-700">{MEDIA_TABLE_MISSING_TEXT}</p>
                            ) : bildHochgeladen ? (
                                <p className="text-xs text-emerald-700 font-medium">Bild hochgeladen — es ist jetzt das Titelbild des Projekts.</p>
                            ) : (
                                <>
                                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => void handleFile(e.target.files)} />
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        className="w-full sm:w-auto"
                                        loading={uploadLaeuft}
                                        disabled={uploadLaeuft || !projectId}
                                        onClick={() => fileInputRef.current?.click()}
                                        icon={uploadLaeuft ? <Loader2 className="w-4 h-4" /> : <ImagePlus className="w-4 h-4" />}
                                    >
                                        Bild auswählen oder fotografieren
                                    </Button>
                                    {bildFehler && <p className="text-xs text-rose-700 whitespace-pre-line">{bildFehler}</p>}
                                </>
                            )}
                        </Abschnitt>
                    )}
                </div>

                <div className="p-4 border-t border-slate-100 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 shrink-0 pb-[max(1rem,env(safe-area-inset-bottom))]">
                    <Button variant="ghost" className="w-full sm:w-auto" onClick={onClose}>Später erinnern</Button>
                    <Button variant="primary" className="w-full sm:w-auto" disabled={!etwasZuSpeichern || gespeichert} onClick={speichern}>
                        Speichern
                    </Button>
                </div>
            </div>
        </div>
    );
};
